import { useMemo } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useOptionalAuth } from "../auth/AuthProvider.js";
import {
  useAskQuestion,
  useMessages,
  useOpportunity,
  useSendMessage,
  useSubmitQuote,
  useUpdateJobStatus,
  type OpportunityView,
} from "../opportunities/api.js";
import { OpportunityDetailBody } from "../opportunities/OpportunityDetailBody.js";
import { usePortalMe } from "../portal/api.js";
import { BrandLogo } from "../ui/BrandLogo.js";

/**
 * The magic-link page (build-plan unit 10). The invitation token arrives in
 * the URL fragment (#t=...), is held in memory for the session, and is sent
 * only as a request header — never written to storage and never placed in a
 * URL this page navigates to.
 *
 * Unit 17: when a session exists AND the account is linked to a contractor,
 * the link lands in the portal context instead (same opportunity, ownership
 * auth, persistent dashboard around it). Signed-out visitors get the page
 * exactly as before, plus a sign-in prompt. The fragment token is dropped
 * by the redirect on purpose — the portal authorizes by ownership. If the
 * signed-in account is NOT the invitee, the portal page shows not-found
 * with guidance to open the link signed out.
 */
export function Opportunity() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const location = useLocation();
  // useMemo, not useEffect+state: the token must be available on first
  // render and must not survive into navigation state anywhere.
  const token = useMemo(() => {
    const match = /(?:^|[#&])t=([A-Za-z0-9_-]+)/.exec(location.hash);
    return match?.[1] ?? null;
  }, [location.hash]);

  // Tolerates the missing provider (unit tests render this page bare);
  // in the app there is always a provider.
  const auth = useOptionalAuth();
  const { data: me } = usePortalMe(auth?.status === "signed-in");

  const { data, isLoading } = useOpportunity(invitationId, token);

  if (auth?.status === "signed-in" && me?.contractor) {
    return <Navigate to={`/portal/opportunities/${invitationId}`} replace />;
  }

  if (!token) return <ClosedState kind="not_found" />;
  if (isLoading || !data) return <LoadingState />;
  if (data.state !== "ok") return <ClosedState kind={data.state} />;
  return (
    <>
      <PublicBody view={data} invitationId={invitationId} token={token} />
      {auth?.status === "signed-out" && (
        <p className="mx-auto max-w-md px-4 pb-10 text-center text-sm text-charcoal">
          <Link to="/sign-in" className="font-semibold text-brand-700">
            Sign in
          </Link>{" "}
          to track all your invitations, quotes, and messages in one place.
        </p>
      )}
    </>
  );
}

function PublicBody({
  view,
  invitationId,
  token,
}: {
  view: OpportunityView;
  invitationId: string | undefined;
  token: string;
}) {
  const submitQuote = useSubmitQuote(invitationId, token);
  const askQuestion = useAskQuestion(invitationId, token);
  const messages = useMessages(invitationId, token);
  const sendMessage = useSendMessage(invitationId, token);
  const updateJobStatus = useUpdateJobStatus(invitationId, token);

  return (
    <OpportunityDetailBody
      view={view}
      brandHeader
      quoteAction={submitQuote}
      questionAction={askQuestion}
      jobAction={updateJobStatus}
      thread={{
        threadOpen: messages.data?.threadOpen ?? false,
        messages: messages.data?.messages ?? [],
        send: sendMessage,
      }}
      quoteCtaLabel="Send quote — no account needed"
    />
  );
}

function LoadingState() {
  return (
    <main className="mx-auto max-w-md px-4 py-10">
      <div className="h-6 w-1/2 animate-pulse rounded bg-charcoal/15" />
      <div className="mt-4 h-32 animate-pulse rounded-2xl bg-charcoal/15" />
      <div className="mt-4 h-48 animate-pulse rounded-2xl bg-charcoal/15" />
    </main>
  );
}

function ClosedState({ kind }: { kind: "not_found" | "closed" | "expired" | "error" }) {
  const copy = {
    not_found: {
      title: "This link isn't valid",
      body: "Check that you opened the most recent email from us — older links stop working when a new one is sent.",
    },
    closed: {
      title: "This request has been filled",
      body: "The customer's request is no longer open for quotes. Thanks for taking a look — we'll send you the next job that matches your trade and service area.",
    },
    expired: {
      title: "This invitation has expired",
      body: "The quoting window for this request has passed. We'll send you the next job that matches your trade and service area.",
    },
    error: {
      title: "Something went wrong",
      body: "We couldn't load this request. Give it a minute and open the link from your email again.",
    },
  }[kind];

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 px-6 py-12">
      <BrandLogo className="mb-2" />
      <h1 className="text-2xl font-bold text-charcoal">{copy.title}</h1>
      <p className="text-base leading-relaxed text-charcoal">{copy.body}</p>
      {(kind === "closed" || kind === "expired") && (
        // The Group 1 -> Group 2 conversion hook (unit 10) now has a real
        // destination: the portal shipped in units 15-17.
        <div className="flex flex-col gap-3">
          <p className="text-sm text-charcoal">
            Keep every invitation, quote, and customer conversation in one place with a free pro
            account.
          </p>
          <Link
            to="/sign-up"
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800"
          >
            Create your pro account
          </Link>
          <p className="text-sm text-charcoal">
            Already have one?{" "}
            <Link to="/sign-in" className="font-semibold text-brand-700">
              Sign in
            </Link>
          </p>
        </div>
      )}
    </main>
  );
}
